import html from './home.html';

import {partial} from '../../../utils/partial';
import {emitter, EVENTS} from '../../../utils/emitter';
import {TweenMax, Power2, TimelineMax} from 'gsap';
import {contentContainer, language, getLanguage} from '../../../configs/global';
import {Router} from '../../../utils/router';
import {Carousel} from '../../../modules/carousel/carousel';
import {Header} from '../../components/header/header';
import {Footer} from '../../components/footer/footer';
import {Story} from '../../pages/story/story';
import homeData from '../../../configs/home';
import copyData from '../../../configs/copy';
import Mustache from 'mustache';
import {state} from '../../../utils/state';
import {Window} from '../../../utils/controllers/window';

export class Home {
  public element: HTMLElement;
  public scrollContainer: HTMLElement;
  public storyPlaceholder: HTMLElement;
  public currentStory: Story;
  public lastStory: Story;
  public carousel: Carousel;
  public header: Header;
  public footer: Footer;
  public bottomOfPage: number;

  private carouselPlaceholder: HTMLElement;
  private scrollHeight: HTMLElement;
  private nextStoryContainer: HTMLElement;
  private nextStoryPlaceholder: HTMLElement;
  private isCarouselAtTop = true;
  private animatingOutStory = false;
  private scrollProgressAmount: number = 0;
  private scrollProgressBar: HTMLElement;

  constructor(header: Header, footer: Footer) {
    this.element = partial(
      Mustache.render(html, Object.assign({}, homeData[getLanguage()], copyData[getLanguage()])),
      '#home'
    );
    const parent = document.querySelector(contentContainer);
    parent.appendChild(this.element);

    this.scrollContainer = this.element.querySelector('.scrollContainer');

    this.carouselPlaceholder = this.element.querySelector('.carouselPlaceholder');
    this.storyPlaceholder = this.element.querySelector('.storyPlaceholder');
    this.nextStoryContainer = this.element.querySelector('.nextStoryContainer');
    this.nextStoryPlaceholder = this.element.querySelector('.nextStoryPlaceholder');
    this.scrollHeight = this.element.querySelector('.scrollHeight');
    this.scrollProgressBar = this.element.querySelector('.bar.progress');

    this.header = header;
    this.footer = footer;

    this.carousel = new Carousel(
      this.element.querySelector('.carousel'),
      homeData[getLanguage()].carouselItems,
      this.element.querySelector('.carouselDockedInfo'),
      this.element.querySelector('.cursors')
    );

    this.carousel.on(EVENTS.select, this.carouselItemSelectHandler);
    this.carousel.init();
    this.carousel.disableScroll();

    emitter.on(EVENTS.resize, this.onResize);
    this.scrollContainer.addEventListener('scroll', this.onScroll);
    this.onResize();
  }

  private carouselItemSelectHandler = e => {
    if (this.isCarouselAtTop) {
      Router.route('/' + e.storyId);
    } else {
      this.zoomNextStory(e.storyId);
    }
  };

  private zoomNextStory(id) {
    this.carousel.zoom(1);
    TweenMax.to(this.scrollContainer, 1, {scrollTo: {y: this.bottomOfPage}});
    TweenMax.delayedCall(1, () => {
      Router.route('/' + id);
    });
  }

  public animateIn() {
    this.element.style.display = `block`;
    this.scrollContainer.scrollTop = 0;
    this.carousel.animateIn();
    TweenMax.to(this.element, 0.5, {opacity: 1});
  }

  public async animateOut() {
    this.header.animateToBlack();
    return new Promise(resolve => {
      TweenMax.to(this.element, 0.5, {
        opacity: 0,
        onComplete: () => {
          this.element.style.display = `none`;
          resolve();
        }
      });
    });
  }

  private onResize = () => {
    this.bottomOfPage = Math.round(this.scrollHeight.clientHeight - Window.height);
    TweenMax.set([this.carouselPlaceholder], {
      height: this.carousel.carouselItemContainer.clientHeight
    });
    TweenMax.set([this.nextStoryPlaceholder], {
      height: Window.height
    });
  };

  private onScroll = () => {
    const scrollTop = this.scrollContainer.scrollTop;

    if (scrollTop > this.bottomOfPage - Window.height * 2 && this.isCarouselAtTop) {
      this.moveCarouselToBottom();
      this.carousel.zoomOut(0);
      this.carousel.gotoSlideById(this.carousel.getNextItem().id, 0);
      this.carousel.disableScroll();
      this.carousel.disableDrag();
      this.carousel.hideOtherSlides();
    } else if (scrollTop < this.bottomOfPage - Window.height * 2 && !this.isCarouselAtTop) {
      this.moveCarouselToTop();
      this.carousel.gotoSlideById(this.currentStory.id, 0);
      this.carousel.showSlides();
      if (this.animatingOutStory) {
        this.carousel.zoomOut(0);
      } else {
        this.carousel.zoom(0);
      }
      this.carousel.enableDrag();
      this.carousel.enableScroll();
    }

    // Show footer when bottom reached
    if (this.bottomOfPage - scrollTop) {
      this.footer.hide();
    } else {
      this.footer.show();
    }
  };

  private onWheel = e => {
    if (!(this.bottomOfPage - this.scrollContainer.scrollTop) && e.delta > 0 && !this.animatingOutStory) {
      TweenMax.killTweensOf(this);
      TweenMax.to(this, 0.5, {scrollProgress: 0, delay: 2});
      this.scrollProgress = this.scrollProgress + e.delta / 35;
    } else if (e.delta < 0) {
      TweenMax.killTweensOf(this);
      this.scrollProgress = 0;
    }
  };

  private set scrollProgress(n) {
    this.scrollProgressAmount = n;
    if (this.scrollProgressAmount > 100) {
      TweenMax.killTweensOf(this);
      emitter.off(EVENTS.wheel, this.onWheel);
      this.scrollProgressBar.style.width = '100px';
      this.zoomNextStory(this.carousel.getCurrentItem().id);
    } else {
      this.scrollProgressBar.style.width = n + 'px';
    }
  }

  private get scrollProgress() {
    return this.scrollProgressAmount;
  }

  /**
   * Show everything without animating
   */
  public show() {
    this.element.style.display = `block`;
    TweenMax.set(this.element, {opacity: 1});
    this.scrollContainer.scrollTop = 0;

    this.carousel.show();
    this.carousel.enableScroll();

    this.header.show();
    this.footer.show();

    this.onResize();
  }

  public setStory(story) {
    this.lastStory = this.currentStory;
    this.currentStory = story;
    this.nextStoryContainer.style.display = 'block';
    this.scrollContainer.style.overflowY = 'scroll';
    this.onResize();
  }

  public animateInStory() {
    if (!this.currentStory) return;
    if (this.lastStory) {
      if (this.isCarouselAtTop) {
        this.animateFromStoryToStory();
      } else {
        this.animateFromStoryToNextStory();
      }
    } else {
      this.animateFromSectionToStory();
    }
  }

  private async animateFromStoryToStory() {
    this.carousel.disableScroll();
    await this.animateOut();
    this.carousel.zoomOut(0);
    this.lastStory.kill();
    this.animateIn();
    this.carousel.gotoSlideById(this.currentStory.id, 0);
    this.carousel.zoom(0);
    this.header.animateToWhite(0);
    this.currentStory.animateIn();
    this.onResize();
  }

  private animateFromStoryToNextStory() {
    this.lastStory.kill();
    this.moveCarouselToTop();
    this.carousel.showSlides();
    this.carousel.enableDrag();
    this.scrollContainer.scrollTop = 0;
    this.currentStory.animateIn();
  }

  private async animateFromSectionToStory() {
    this.carousel.disableScroll();
    this.show();
    let speed = state.getValue('first') ? 0 : 1;
    await this.carousel.gotoSlideById(this.currentStory.id, speed);
    this.carousel.zoom(speed);
    this.header.animateToWhite(0.5);
    this.currentStory.animateIn();
  }

  private moveCarouselToBottom() {
    if (this.isCarouselAtTop) {
      this.scrollProgress = 0;
      emitter.off(EVENTS.wheel, this.onWheel);
      emitter.on(EVENTS.wheel, this.onWheel);
      this.isCarouselAtTop = false;
      this.nextStoryPlaceholder.appendChild(this.carousel.element);
    }
    this.onResize();
  }

  private moveCarouselToTop() {
    if (!this.isCarouselAtTop) {
      this.isCarouselAtTop = true;
      this.carouselPlaceholder.appendChild(this.carousel.element);
    }
    this.onResize();
  }

  public async removeStory() {
    if (this.currentStory) {
      this.animatingOutStory = true;
      this.header.animateToBlack();
      this.carousel.zoomOut(1);
      await this.currentStory.animateOutScroll();
      this.nextStoryContainer.style.display = 'none';
      this.scrollContainer.style.overflowY = 'hidden';
      this.currentStory.kill();
      this.currentStory = null;
      this.animatingOutStory = false;
    }
  }

  public resize() {
    this.onResize();
  }

  public kill() {
    this.removeStory();
  }
}
